import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, X, Search, Bookmark, Download, Pencil, Users, Phone, Link2, Copy, Check, ArrowUpDown, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { supabase } from './supabaseClient';
import { generateDirectoryPDF } from './generateDirectoryPDF';
import DirectoryExportModal from './DirectoryExportModal';
import { UNITS } from './constants';

const DIRECTORY_ITEM_TYPES = ['Робота', 'Матеріали', 'Комплектація', 'Транспорт', 'Інші послуги'];
const SPECS = [
  "Демонтажні роботи", "Вантажні роботи", "Вивезення сміття", "Перевезення",
  "Кондиціювання", "Вентиляція", "Гіпсокартонні роботи", "Електромонтажні роботи",
  "Сантехнічні роботи", "Столярні роботи", "Натяжна стеля", "Малярні роботи",
  "Загальнобудівельні роботи", "Плиточні роботи", "Ландшафтні роботи",
  "Бетонні роботи", "Зварювальні роботи"
];

// ==================== DIRECTORY MODAL COMPONENT ====================

const DirectoryModal = ({ 
  isOpen, 
  onClose, 
  onAddToEstimate,
  estimateSections,
  templates = [],
  setTemplates,
  confirmModal,
  setConfirmModal,
  branding,
  setPreviewFile,
  isPro,
  showPaywall
}) => {
  const [tab, setTab] = useState('items'); // 'items' | 'market' | 'contractors'
  const [sections, setSections] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedSections, setExpandedSections] = useState([]);
  
  // Сортування
  const [sortBy, setSortBy] = useState('default'); // 'default' | 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc'
  const [showSortMenu, setShowSortMenu] = useState(false);
  
  // Ринкові ціни — з Supabase
  const [marketData, setMarketData] = useState([]);
  const [marketLastUpdated, setMarketLastUpdated] = useState('');
  const [marketLoading, setMarketLoading] = useState(false);

  // Довідник LIHTAR — з Supabase
  const [lihtarData, setLihtarData] = useState([]);
  const [lihtarLoading, setLihtarLoading] = useState(false);
  
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  
  const [showAddItem, setShowAddItem] = useState(false);
  const [addItemToSectionId, setAddItemToSectionId] = useState(null);
  const [newItem, setNewItem] = useState({ position: '', unit: 'шт.', price: '', type: 'Робота' });
  
  const [showRenameSection, setShowRenameSection] = useState(false);
  const [renameSectionId, setRenameSectionId] = useState(null);
  const [renameSectionName, setRenameSectionName] = useState('');
  
  const [showSelectEstimateSection, setShowSelectEstimateSection] = useState(false);
  const [newEstimateSectionName, setNewEstimateSectionName] = useState('');
  
  const [showAddToTemplate, setShowAddToTemplate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplateSectionId, setSelectedTemplateSectionId] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateSectionName, setNewTemplateSectionName] = useState('');
  
  const [showEditItem, setShowEditItem] = useState(false);
  const [editItemSectionId, setEditItemSectionId] = useState(null);
  const [editItem, setEditItem] = useState({ id: null, position: '', unit: 'шт.', price: '', type: 'Робота' });

  // Додати з ринкових в довідник — вибір розділу
  const [showAddMarketToDir, setShowAddMarketToDir] = useState(false);
  const [pendingMarketItem, setPendingMarketItem] = useState(null);
  const [newMarketDirSectionName, setNewMarketDirSectionName] = useState('');

  // Contractors state
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [newContractor, setNewContractor] = useState({ name: '', phone: '', spec: SPECS[0] });
  const [editContractorId, setEditContractorId] = useState(null);
  const [editContractor, setEditContractor] = useState({ name: '', phone: '', spec: '' });

  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [shareFullCopied, setShareFullCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  // Exclusion-модель: за замовчуванням усі публічні розділи в посиланні. Юзер зніма чекбоксом — додається в excluded.
  const [excludedShareIds, setExcludedShareIds] = useState(() => new Set());
  const [showExportModal, setShowExportModal] = useState(false);

  // ==================== LOAD DATA FROM SUPABASE ====================
  const loadData = useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    // Load sections with items
    const { data: secs } = await supabase.from('user_directory').select('*').eq('user_id', user.id).order('sort_order');
    const { data: items } = await supabase.from('user_directory_items').select('*').eq('user_id', user.id).order('sort_order');
    
    const sectionsWithItems = (secs || []).map(s => ({
      ...s,
      items: (items || []).filter(i => i.section_id === s.id)
    }));
    setSections(sectionsWithItems);

    // Load contractors
    const { data: ctrs } = await supabase.from('user_contractors').select('*').eq('user_id', user.id).order('created_at');
    setContractors(ctrs || []);

    // Migrate from localStorage if exists
    const saved = localStorage.getItem('onero_directory');
    if (saved && sectionsWithItems.length === 0) {
      try {
        const old = JSON.parse(saved);
        if (old.sections && old.sections.length > 0) {
          for (const s of old.sections) {
            const { data: newSec } = await supabase.from('user_directory').insert({ user_id: user.id, name: s.name, sort_order: 0 }).select().single();
            if (newSec && s.items?.length > 0) {
              const rows = s.items.map((it, idx) => ({
                section_id: newSec.id, user_id: user.id,
                position: it.position, unit: it.unit || 'шт.',
                price: it.price || 0, type: it.type || 'Робота', sort_order: idx
              }));
              await supabase.from('user_directory_items').insert(rows);
            }
          }
          localStorage.removeItem('onero_directory');
          // Reload after migration
          const { data: secs2 } = await supabase.from('user_directory').select('*').eq('user_id', user.id).order('sort_order');
          const { data: items2 } = await supabase.from('user_directory_items').select('*').eq('user_id', user.id).order('sort_order');
          setSections((secs2 || []).map(s => ({ ...s, items: (items2 || []).filter(i => i.section_id === s.id) })));
        }
      } catch (e) { console.error('Migration error', e); }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setSelectedItems([]);
      setSearch('');
    }
  }, [isOpen, loadData]);

  // Завантаження ринкових цін з Supabase
  useEffect(() => {
    if (isOpen && (tab === 'market' || marketData.length === 0)) {
      setMarketLoading(true);
      (async () => {
        const { data } = await supabase
          .from('market_prices')
          .select('*')
          .order('category')
          .order('position');
        if (data && data.length > 0) {
          const grouped = {};
          data.forEach(item => {
            if (!grouped[item.category]) grouped[item.category] = [];
            grouped[item.category].push({
              id: 'm' + item.id,
              position: item.position,
              unit: item.unit,
              min: Number(item.price_min),
              max: Number(item.price_max),
              avg: Number(item.price_avg),
            });
          });
          setMarketData(Object.entries(grouped).map(([cat, items]) => ({ cat, items })));
          setMarketLastUpdated(new Date(data[0].updated_at).toLocaleDateString('uk-UA'));
        }
        setMarketLoading(false);
      })();
    }
  }, [isOpen, tab]);

  // Завантаження довідника LIHTAR з Supabase
  useEffect(() => {
    if (isOpen && (tab === 'lihtar' || lihtarData.length === 0)) {
      setLihtarLoading(true);
      (async () => {
        const { data } = await supabase
          .from('lihtar_prices')
          .select('*')
          .order('sort_order');
        if (data && data.length > 0) {
          const grouped = {};
          data.forEach(item => {
            if (!grouped[item.section]) grouped[item.section] = [];
            grouped[item.section].push({
              id: 'lp' + item.id,
              position: item.position,
              unit: item.unit,
              min: Number(item.price_min),
              max: Number(item.price_max),
              avg: Number(item.price_avg),
            });
          });
          setLihtarData(Object.entries(grouped).map(([cat, items]) => ({ cat, items })));
        }
        setLihtarLoading(false);
      })();
    }
  }, [isOpen, tab]);

  // ==================== DIRECTORY ACTIONS ====================
  const addSection = async () => {
    if (!newSectionName.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('user_directory').insert({ user_id: user.id, name: newSectionName.trim(), sort_order: sections.length }).select().single();
    if (data) setSections(prev => [...prev, { ...data, items: [] }]);
    setNewSectionName('');
    setShowAddSection(false);
  };

  const deleteSection = async (sectionId) => {
    await supabase.from('user_directory_items').delete().eq('section_id', sectionId);
    await supabase.from('user_directory').delete().eq('id', sectionId);
    setSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const renameSection = async () => {
    if (!renameSectionName.trim()) return;
    await supabase.from('user_directory').update({ name: renameSectionName.trim() }).eq('id', renameSectionId);
    setSections(prev => prev.map(s => s.id === renameSectionId ? { ...s, name: renameSectionName.trim() } : s));
    setRenameSectionName(''); setRenameSectionId(null); setShowRenameSection(false);
  };

  const toggleSectionPrivate = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const newVal = !section.is_private;
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, is_private: newVal } : s));
    // Якщо стає приватним — прибираємо з excluded (exclusion тільки для публічних)
    if (newVal) setExcludedShareIds(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
    const { error } = await supabase.from('user_directory').update({ is_private: newVal }).eq('id', sectionId);
    if (error) {
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, is_private: !newVal } : s));
      if (window.showToast) window.showToast('Не вдалось змінити приватність', 'error');
    }
  };

  const toggleShareInclusion = (sectionId) => {
    setExcludedShareIds(prev => {
      const n = new Set(prev);
      if (n.has(sectionId)) n.delete(sectionId); else n.add(sectionId);
      return n;
    });
  };

  const getShareIncludedSections = () => sections.filter(s => !s.is_private && !excludedShareIds.has(s.id));
  const buildShareUrl = (includeSelection) => {
    if (!shareLink) return '';
    const base = shareLink.split('?')[0];
    if (!includeSelection) return base;
    const keys = getShareIncludedSections().map(s => s.share_key).filter(Boolean);
    if (keys.length === 0) return base; // порожній вибір — повертаємо base
    const publicCount = sections.filter(s => !s.is_private).length;
    if (keys.length === publicCount) return base; // усі публічні обрані — без ?s= (чистий URL)
    return `${base}?s=${keys.join(',')}`;
  };

  const addItem = async () => {
    if (!newItem.position.trim() || !addItemToSectionId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const sec = sections.find(s => s.id === addItemToSectionId);
    const row = {
      section_id: addItemToSectionId, user_id: user.id,
      position: newItem.position.trim().charAt(0).toUpperCase() + newItem.position.trim().slice(1), unit: newItem.unit,
      price: Math.round((parseFloat(newItem.price) || 0) * 100) / 100,
      type: newItem.type, sort_order: sec ? sec.items.length : 0
    };
    const { data } = await supabase.from('user_directory_items').insert(row).select().single();
    if (data) setSections(prev => prev.map(s => s.id === addItemToSectionId ? { ...s, items: [...s.items, data] } : s));
    setNewItem({ position: '', unit: 'шт.', price: '', type: 'Робота' }); setShowAddItem(false); setAddItemToSectionId(null);
  };

  const deleteItem = async (sectionId, itemId) => {
    await supabase.from('user_directory_items').delete().eq('id', itemId);
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, items: s.items.filter(i => i.id !== itemId) } : s));
    setSelectedItems(prev => prev.filter(k => k !== `${sectionId}-${itemId}`));
  };

  const openEditItem = (sectionId, item) => {
    setEditItemSectionId(sectionId);
    setEditItem({ id: item.id, position: item.position, unit: item.unit || 'шт.', price: item.price ? String(Math.round(item.price * 100) / 100) : '', type: item.type || 'Робота' });
    setShowEditItem(true);
  };

  const saveEditItem = async () => {
    if (!editItem.position.trim() || !editItemSectionId) return;
    const upd = { position: editItem.position.trim().charAt(0).toUpperCase() + editItem.position.trim().slice(1), unit: editItem.unit, price: Math.round((parseFloat(editItem.price) || 0) * 100) / 100, type: editItem.type };
    await supabase.from('user_directory_items').update(upd).eq('id', editItem.id);
    setSections(prev => prev.map(s => s.id === editItemSectionId ? { ...s, items: s.items.map(i => i.id === editItem.id ? { ...i, ...upd } : i) } : s));
    setShowEditItem(false); setEditItemSectionId(null); setEditItem({ id: null, position: '', unit: 'шт.', price: '', type: 'Робота' });
  };

  // ==================== CONTRACTOR ACTIONS ====================
  const addContractorAction = async () => {
    if (!newContractor.name.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('user_contractors').insert({ user_id: user.id, name: newContractor.name.trim(), phone: newContractor.phone.trim(), spec: newContractor.spec }).select().single();
    if (data) setContractors(prev => [...prev, data]);
    setNewContractor({ name: '', phone: '', spec: SPECS[0] }); setShowAddContractor(false);
  };

  const deleteContractor = async (id) => {
    await supabase.from('user_contractors').delete().eq('id', id);
    setContractors(prev => prev.filter(c => c.id !== id));
  };

  const startEditContractor = (c) => {
    setEditContractorId(c.id);
    setEditContractor({ name: c.name, phone: c.phone || '', spec: c.spec || '' });
  };

  const saveContractor = async () => {
    if (!editContractor.name.trim()) return;
    const upd = { name: editContractor.name.trim(), phone: editContractor.phone.trim(), spec: editContractor.spec };
    await supabase.from('user_contractors').update(upd).eq('id', editContractorId);
    setContractors(prev => prev.map(c => c.id === editContractorId ? { ...c, ...upd } : c));
    setEditContractorId(null);
  };

  // ==================== SHARE LINK ====================
  const generateShareLink = async () => {
    setShareLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Перевіряємо чи вже є
      const { data: existing } = await supabase
        .from('shared_directory')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (existing) {
        setShareLink(`${window.location.origin}/price/${existing.share_token}`);
        setShowShareModal(true);
        setShareLoading(false);
        return;
      }

      // Створюємо новий
      const { data, error } = await supabase
        .from('shared_directory')
        .insert({ user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      setShareLink(`${window.location.origin}/price/${data.share_token}`);
      setShowShareModal(true);
    } catch (e) {
      console.error('Share error:', e);
    }
    setShareLoading(false);
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(buildShareUrl(true));
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const copyFullShareLink = () => {
    navigator.clipboard.writeText(buildShareUrl(false));
    setShareFullCopied(true);
    setTimeout(() => setShareFullCopied(false), 2000);
  };

  // ==================== ESTIMATE ACTIONS ====================
  const handleAddToEstimate = (targetSectionId, createNew = false, sectionName = '') => {
    if (!onAddToEstimate) return;
    const itemsToAdd = [];
    // Позиції з особистого довідника
    sections.forEach(section => {
      section.items.forEach(item => {
        if (selectedItems.includes(`${section.id}-${item.id}`)) {
          itemsToAdd.push({
            id: Date.now() + Math.random(),
            type: item.type || 'Робота', position: item.position, unit: item.unit || 'шт.', qty: 1,
            workPrice: 0, markup: 0,
            clientPrice: Math.round((item.price || 0) * 100) / 100, priceLock: true
          });
        }
      });
    });
    // Позиції з ринкових цін
    marketData.forEach(cat => {
      cat.items.forEach(item => {
        if (selectedItems.includes(`market-${item.id}`)) {
          const avgPrice = item.avg || Math.round((item.min + item.max) / 2);
          itemsToAdd.push({
            id: Date.now() + Math.random(),
            type: 'Робота', position: item.position, unit: item.unit || 'шт.', qty: 1,
            workPrice: 0, markup: 0,
            clientPrice: avgPrice, priceLock: true
          });
        }
      });
    });
    if (itemsToAdd.length === 0) return;
    onAddToEstimate(itemsToAdd, targetSectionId, createNew, sectionName);
    setSelectedItems([]); setShowSelectEstimateSection(false); onClose();
  };

  if (!isOpen) return null;
  
  const isViewMode = !onAddToEstimate && !setTemplates;
  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
  const filteredContractors = contractors.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.spec || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-3xl w-full max-h-[95vh] sm:max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-5 border-b flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                {tab === 'items' ? <Bookmark className="w-5 h-5 text-amber-600" /> : <Users className="w-5 h-5 text-amber-600" />}
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: '#163248' }}>Довідник</h3>
                <p className="text-xs text-gray-500">{tab === 'items' ? `${totalItems} позицій` : tab === 'market' ? `${marketData.reduce((s, c) => s + c.items.length, 0)} позицій` : tab === 'lihtar' ? `${lihtarData.reduce((s, c) => s + c.items.length, 0)} позицій` : `${contractors.length} підрядників`}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b flex-shrink-0">
            <button onClick={() => { setTab('items'); setSearch(''); setSortBy('default'); }} className={`flex-1 py-2.5 text-sm font-medium transition-colors ${tab === 'items' ? 'border-b-2 border-amber-500 text-amber-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Bookmark className="w-4 h-4 inline mr-1.5 -mt-0.5" />Мої позиції
            </button>
            <button onClick={() => { setTab('lihtar'); setSearch(''); setSortBy('default'); }} className={`flex-1 py-2.5 text-sm font-medium transition-colors ${tab === 'lihtar' ? 'border-b-2 text-gray-700' : 'text-gray-500 hover:text-gray-700'}`} style={tab === 'lihtar' ? { borderColor: '#163248', color: '#163248' } : {}}>
              <Bookmark className="w-4 h-4 inline mr-1.5 -mt-0.5" />Довідник LIHTAR
            </button>
            <button onClick={() => { setTab('market'); setSearch(''); setSortBy('default'); }} className={`flex-1 py-2.5 text-sm font-medium transition-colors ${tab === 'market' ? 'border-b-2 text-gray-700' : 'text-gray-500 hover:text-gray-700'}`} style={tab === 'market' ? { borderColor: '#38b6ff', color: '#38b6ff' } : {}}>
              <Bookmark className="w-4 h-4 inline mr-1.5 -mt-0.5" />Ринкові ціни
            </button>
            <button onClick={() => { if (!isPro) { showPaywall('directory_full'); return; } setTab('contractors'); setSearch(''); setSortBy('default'); }} className={`flex-1 py-2.5 text-sm font-medium transition-colors ${tab === 'contractors' ? 'border-b-2 border-amber-500 text-amber-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />Підрядники
            </button>
          </div>
          
          {/* Toolbar */}
          <div className="p-4 border-b flex flex-wrap items-center gap-3 flex-shrink-0">
            <div className="w-full sm:flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" placeholder={tab === 'contractors' ? "Пошук підрядників..." : "Пошук позицій..."} value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            {(tab === 'items' || tab === 'market' || tab === 'lihtar') && (
              <div className="relative">
                <button onClick={() => setShowSortMenu(!showSortMenu)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium transition-colors" style={{ color: sortBy !== 'default' ? '#163248' : '#9ca3af' }}>
                  <ArrowUpDown className="w-4 h-4" />
                  <span className="hidden sm:inline">{sortBy === 'name-asc' ? 'А → Я' : sortBy === 'name-desc' ? 'Я → А' : sortBy === 'price-asc' ? 'Дешевше' : sortBy === 'price-desc' ? 'Дорожче' : 'Сортувати'}</span>
                </button>
                {showSortMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 w-40">
                      {[
                        { key: 'default', label: 'За замовчуванням' },
                        { key: 'name-asc', label: 'А → Я' },
                        { key: 'name-desc', label: 'Я → А' },
                        { key: 'price-asc', label: 'Ціна ↑' },
                        { key: 'price-desc', label: 'Ціна ↓' },
                      ].map(opt => (
                        <button key={opt.key} onClick={() => { setSortBy(opt.key); setShowSortMenu(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${sortBy === opt.key ? 'font-medium' : 'text-gray-600'}`}
                          style={sortBy === opt.key ? { color: '#163248' } : {}}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {tab === 'items' && (
              <>
                <button onClick={() => setShowAddSection(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium">
                  <Plus className="w-4 h-4" /> Розділ
                </button>
                <button onClick={() => {
                  if (!isPro) { showPaywall('directory_full'); return; }
                  if (sections.length === 0) return;
                  setShowExportModal(true);
                }} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium" style={{ color: '#163248' }}>
                  <Download className="w-4 h-4" /> Експорт
                </button>
                <button onClick={() => { if (!isPro) { showPaywall('directory_full'); return; } generateShareLink(); }} disabled={shareLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium" style={{ color: '#163248' }}>
                  <Link2 className="w-4 h-4" /> {shareLoading ? '...' : 'Посилання'}
                </button>
              </>
            )}
            {tab === 'contractors' && (
              <button onClick={() => setShowAddContractor(!showAddContractor)} className="px-3 py-1.5 text-xs rounded-xl font-semibold text-white" style={{ background: '#163248' }}>
                + Додати
              </button>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading && tab !== 'market' ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-400">Завантаження...</p>
              </div>
            ) : tab === 'items' ? (
              /* ==================== ITEMS TAB ==================== */
              sections.length === 0 ? (
                <div className="text-center py-12">
                  <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">Довідник порожній</p>
                  <p className="text-sm text-gray-400">Створіть розділ та додайте позиції</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {!isViewMode && sections.some(s => !s.is_private) && (() => {
                    const publicCount = sections.filter(s => !s.is_private).length;
                    const includedCount = sections.filter(s => !s.is_private && !excludedShareIds.has(s.id)).length;
                    const allExcluded = includedCount === 0;
                    return (
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs" style={{ color: '#6b7280' }}>
                        <span><span style={{ color: '#163248', fontWeight: 600 }}>{includedCount}</span> з {publicCount} розділів у посиланні</span>
                        <div className="flex items-center gap-3">
                          {!allExcluded && (
                            <button onClick={() => setExcludedShareIds(new Set(sections.filter(s => !s.is_private).map(s => s.id)))} className="hover:underline" style={{ color: '#f97316' }}>Виключити всі</button>
                          )}
                          {excludedShareIds.size > 0 && (
                            <button onClick={() => setExcludedShareIds(new Set())} className="hover:underline" style={{ color: '#f97316' }}>Включити всі</button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {sections.map(section => {
                    const isExpanded = expandedSections.includes(section.id);
                    let filteredItems = section.items.filter(item => !search || item.position.toLowerCase().includes(search.toLowerCase()));
                    // Сортування
                    if (sortBy === 'name-asc') filteredItems = [...filteredItems].sort((a, b) => a.position.localeCompare(b.position, 'uk'));
                    else if (sortBy === 'name-desc') filteredItems = [...filteredItems].sort((a, b) => b.position.localeCompare(a.position, 'uk'));
                    else if (sortBy === 'price-asc') filteredItems = [...filteredItems].sort((a, b) => (a.price || 0) - (b.price || 0));
                    else if (sortBy === 'price-desc') filteredItems = [...filteredItems].sort((a, b) => (b.price || 0) - (a.price || 0));
                    if (search && filteredItems.length === 0) return null;
                    
                    return (
                      <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden" style={{ position: 'relative' }}>
                        {section.is_private && (
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 3, background: '#163248', zIndex: 1 }} />
                        )}
                        <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100" style={{ opacity: section.is_private ? 0.75 : 1 }} onClick={() => setExpandedSections(prev => prev.includes(section.id) ? prev.filter(id => id !== section.id) : [...prev, section.id])}>
                          {!isViewMode && (
                            <button onClick={(e) => { e.stopPropagation(); toggleSectionPrivate(section.id); }} className="p-1 hover:bg-gray-200 rounded" title={section.is_private ? 'Зробити публічним' : 'Зробити приватним (не потрапляє в посилання та PDF)'}>
                              {section.is_private ? <EyeOff className="w-4 h-4" style={{ color: '#9ca3af' }} /> : <Eye className="w-4 h-4" style={{ color: '#9ca3af' }} />}
                            </button>
                          )}
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <span className="flex-1 font-medium text-sm" style={{ color: '#163248' }}>{section.name}</span>
                          {section.is_private && (
                            <span style={{ background: '#163248', color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', padding: '3px 7px', borderRadius: 4, marginRight: 4 }}>Приватно</span>
                          )}
                          <span className="text-xs text-gray-400 mr-2">{section.items.length} поз.</span>
                          {!section.is_private && !isViewMode && (
                            <label onClick={(e) => e.stopPropagation()} className="flex items-center cursor-pointer mr-1" title={excludedShareIds.has(section.id) ? 'Не в посиланні' : 'Включено в посилання'}>
                              <input type="checkbox" checked={!excludedShareIds.has(section.id)} onChange={() => toggleShareInclusion(section.id)} className="w-4 h-4 rounded cursor-pointer" />
                            </label>
                          )}
                          <div className="flex items-center gap-1">
                            <button onClick={(e) => { e.stopPropagation(); setAddItemToSectionId(section.id); setShowAddItem(true); }} className="p-1.5 hover:bg-green-100 rounded-lg" title="Додати позицію">
                              <Plus className="w-3.5 h-3.5 text-green-600" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setRenameSectionId(section.id); setRenameSectionName(section.name); setShowRenameSection(true); }} className="p-1.5 hover:bg-gray-200 rounded-lg">
                              <Pencil className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setConfirmModal({ show: true, title: 'Видалити розділ?', message: `Ви впевнені, що хочете видалити розділ "${section.name}"?`, onConfirm: () => deleteSection(section.id) }); }} className="p-1.5 hover:bg-red-100 rounded-lg">
                              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="divide-y">
                            {filteredItems.length === 0 ? (
                              <div className="px-4 py-6 text-center">
                                <p className="text-sm text-gray-400 mb-2">Розділ порожній</p>
                                <button onClick={() => { setAddItemToSectionId(section.id); setShowAddItem(true); }} className="text-sm text-amber-600 hover:text-amber-700 font-medium">+ Додати позицію</button>
                              </div>
                            ) : (
                              filteredItems.map(item => {
                                const itemKey = `${section.id}-${item.id}`;
                                const isSelected = selectedItems.includes(itemKey);
                                return (
                                  <div key={item.id} className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 group ${isSelected ? 'bg-amber-50' : ''}`}>
                                    {!isViewMode && (
                                      <input type="checkbox" checked={isSelected} onChange={() => setSelectedItems(prev => prev.includes(itemKey) ? prev.filter(k => k !== itemKey) : [...prev, itemKey])} className="w-4 h-4 rounded" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium line-clamp-2 break-words" style={{ color: '#163248' }}>{item.position}</div>
                                    </div>
                                    <span className="text-xs text-gray-400 flex-shrink-0 w-10 text-center">{item.unit}</span>
                                    <span className="text-sm font-semibold flex-shrink-0 tabular-nums text-right w-20" style={{ color: '#163248' }}>
                                      {(item.price || 0).toLocaleString('uk-UA', { maximumFractionDigits: 2 })} ₴
                                    </span>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                                      <button onClick={() => openEditItem(section.id, item)} className="p-1 hover:bg-gray-200 rounded" title="Редагувати">
                                        <Pencil className="w-3.5 h-3.5 text-gray-400" />
                                      </button>
                                      <button onClick={() => deleteItem(section.id, item.id)} className="p-1 hover:bg-red-100 rounded" title="Видалити">
                                        <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ) : tab === 'lihtar' ? (
              /* ==================== LIHTAR PRICES TAB ==================== */
              lihtarLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Завантаження цін...</p>
                </div>
              ) : lihtarData.length === 0 ? (
                <div className="text-center py-12">
                  <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">Довідник LIHTAR порожній</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {lihtarData.map((cat, catIdx) => {
                    let filteredItems = cat.items.filter(item => !search || item.position.toLowerCase().includes(search.toLowerCase()));
                    if (sortBy === 'name-asc') filteredItems = [...filteredItems].sort((a, b) => a.position.localeCompare(b.position, 'uk'));
                    else if (sortBy === 'name-desc') filteredItems = [...filteredItems].sort((a, b) => b.position.localeCompare(a.position, 'uk'));
                    else if (sortBy === 'price-asc') filteredItems = [...filteredItems].sort((a, b) => a.min - b.min);
                    else if (sortBy === 'price-desc') filteredItems = [...filteredItems].sort((a, b) => b.max - a.max);
                    if (search && filteredItems.length === 0) return null;
                    const isExpanded = expandedSections.includes('lp-' + catIdx);
                    return (
                      <div key={catIdx} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => setExpandedSections(prev => prev.includes('lp-' + catIdx) ? prev.filter(id => id !== 'lp-' + catIdx) : [...prev, 'lp-' + catIdx])}>
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <span className="flex-1 font-medium text-sm" style={{ color: '#163248' }}>{cat.cat}</span>
                          <span className="text-xs text-gray-400">{cat.items.length} поз.</span>
                        </div>
                        {isExpanded && (
                          <div className="divide-y">
                            {filteredItems.length === 0 ? (
                              <div className="px-4 py-4 text-center text-sm text-gray-400">Нічого не знайдено</div>
                            ) : (
                              filteredItems.map(item => {
                                const itemKey = item.id;
                                const isSelected = selectedItems.includes(itemKey);
                                return (
                                  <div key={item.id} className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 group ${isSelected ? 'bg-blue-50' : ''}`}>
                                    {!isViewMode && onAddToEstimate && (
                                      <input type="checkbox" checked={isSelected} onChange={() => { if (!isPro) { if (showPaywall) showPaywall('directory_full'); return; } setSelectedItems(prev => prev.includes(itemKey) ? prev.filter(k => k !== itemKey) : [...prev, itemKey]); }} className="w-4 h-4 rounded" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium line-clamp-2 break-words" style={{ color: '#163248' }}>{item.position}</div>
                                      <div className="text-[11px] text-gray-400">від {item.min} до {item.max} грн/{item.unit}</div>
                                    </div>
                                    <span className="text-xs text-gray-400 flex-shrink-0 w-10 text-center">{item.unit}</span>
                                    <span className="text-sm font-semibold flex-shrink-0 tabular-nums text-right w-20" style={{ color: '#163248' }}>
                                      {item.avg} ₴
                                    </span>
                                    <button
                                      onClick={() => { if (!isPro) { if (showPaywall) showPaywall('directory_full'); return; } setPendingMarketItem(item); setShowAddMarketToDir(true); setNewMarketDirSectionName(''); }}
                                      className="p-1.5 hover:bg-amber-100 rounded-lg opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity"
                                      title="Додати в мій довідник"
                                    >
                                      <Plus className="w-3.5 h-3.5 text-amber-600" />
                                    </button>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ) : tab === 'market' ? (
              /* ==================== MARKET PRICES TAB ==================== */
              <div>
                {/* Info banner */}
                <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl" style={{ background: '#eff6ff' }}>
                  <Bookmark className="w-5 h-5 flex-shrink-0" style={{ color: '#38b6ff' }} fill="#38b6ff" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs" style={{ color: '#38b6ff' }}>На основі відкритих даних <a href="https://rabotniki.ua" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">rabotniki.ua</a></p>
                    {marketLastUpdated && <p className="text-[11px] text-gray-400 mt-0.5">Оновлено {marketLastUpdated}</p>}
                  </div>
                </div>
                
                {marketLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-400">Завантаження цін...</p>
                  </div>
                ) : marketData.length === 0 ? (
                  <div className="text-center py-12">
                    <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">Ринкові ціни ще не завантажені</p>
                  </div>
                ) : (
                <div className="space-y-3">
                  {marketData.map((cat, catIdx) => {
                    let filteredItems = cat.items.filter(item => !search || item.position.toLowerCase().includes(search.toLowerCase()));
                    if (sortBy === 'name-asc') filteredItems = [...filteredItems].sort((a, b) => a.position.localeCompare(b.position, 'uk'));
                    else if (sortBy === 'name-desc') filteredItems = [...filteredItems].sort((a, b) => b.position.localeCompare(a.position, 'uk'));
                    else if (sortBy === 'price-asc') filteredItems = [...filteredItems].sort((a, b) => a.min - b.min);
                    else if (sortBy === 'price-desc') filteredItems = [...filteredItems].sort((a, b) => b.max - a.max);
                    if (search && filteredItems.length === 0) return null;
                    const isExpanded = expandedSections.includes('market-' + catIdx);
                    
                    return (
                      <div key={catIdx} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => setExpandedSections(prev => prev.includes('market-' + catIdx) ? prev.filter(id => id !== 'market-' + catIdx) : [...prev, 'market-' + catIdx])}>
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          <span className="flex-1 font-medium text-sm" style={{ color: '#38b6ff' }}>{cat.cat}</span>
                          <span className="text-xs text-gray-400">{cat.items.length} поз.</span>
                        </div>
                        
                        {isExpanded && (
                          <div className="divide-y">
                            {filteredItems.length === 0 ? (
                              <div className="px-4 py-4 text-center text-sm text-gray-400">Нічого не знайдено</div>
                            ) : (
                              filteredItems.map(item => {
                                const itemKey = `market-${item.id}`;
                                const isSelected = selectedItems.includes(itemKey);
                                return (
                                  <div key={item.id} className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 group ${isSelected ? 'bg-blue-50' : ''}`}>
                                    {!isViewMode && onAddToEstimate && (
                                      <input type="checkbox" checked={isSelected} onChange={() => { if (!isPro) { if (showPaywall) showPaywall('directory_full'); return; } setSelectedItems(prev => prev.includes(itemKey) ? prev.filter(k => k !== itemKey) : [...prev, itemKey]); }} className="w-4 h-4 rounded" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium line-clamp-2 break-words" style={{ color: '#374151' }}>{item.position}</div>
                                      <div className="text-[11px] text-gray-400">від {item.min} до {item.max} грн/{item.unit}</div>
                                    </div>
                                    <span className="text-xs text-gray-400 flex-shrink-0 w-10 text-center">{item.unit}</span>
                                    <span className="text-sm font-semibold flex-shrink-0 tabular-nums text-right w-20" style={{ color: '#38b6ff' }}>
                                      {item.avg || Math.round((item.min + item.max) / 2)} ₴
                                    </span>
                                    {/* Додати в мій довідник */}
                                    <button
                                      onClick={() => { if (!isPro) { if (showPaywall) showPaywall('directory_full'); return; } setPendingMarketItem(item); setShowAddMarketToDir(true); setNewMarketDirSectionName(''); }}
                                      className="p-1.5 hover:bg-amber-100 rounded-lg opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity"
                                      title="Додати в мій довідник"
                                    >
                                      <Plus className="w-3.5 h-3.5 text-amber-600" />
                                    </button>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            ) : (
              /* ==================== CONTRACTORS TAB ==================== */
              <>
                {/* Inline add form — like GanttContractorsPanel */}
                {showAddContractor && (
                  <div className="px-4 py-4 border-b space-y-2 mb-3 rounded-xl" style={{ background: '#f9fafb' }}>
                    <div className="text-xs font-semibold" style={{ color: '#163248' }}>Новий підрядник</div>
                    <input type="text" value={newContractor.name} onChange={e => setNewContractor(p => ({ ...p, name: e.target.value }))} placeholder="Ім'я / компанія *"
                      className="w-full px-3 py-2 text-xs rounded-lg border outline-none focus:ring-1 focus:ring-blue-300" style={{ borderColor: '#e5e7eb' }} autoFocus
                      onKeyDown={e => { if (e.key === 'Enter' && newContractor.name.trim()) addContractorAction(); }} />
                    <div className="flex gap-2">
                      <input type="text" value={newContractor.phone} onChange={e => setNewContractor(p => ({ ...p, phone: e.target.value }))} placeholder="Телефон"
                        className="flex-1 px-3 py-2 text-xs rounded-lg border outline-none" style={{ borderColor: '#e5e7eb' }} />
                      <select value={newContractor.spec} onChange={e => {
                        if (e.target.value === '__custom__') { const v = prompt('Введіть спеціалізацію:'); if (v) setNewContractor(p => ({ ...p, spec: v })); }
                        else setNewContractor(p => ({ ...p, spec: e.target.value }));
                      }} className="flex-1 px-3 py-2 text-xs rounded-lg border outline-none" style={{ borderColor: '#e5e7eb' }}>
                        {SPECS.map(s => <option key={s} value={s}>{s}</option>)}
                        <option value="__custom__">Ввести свій варіант...</option>
                      </select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => { setShowAddContractor(false); setNewContractor({ name: '', phone: '', spec: SPECS[0] }); }}
                        className="flex-1 py-2 text-xs rounded-xl border font-medium" style={{ borderColor: '#e5e7eb', color: '#6b7280' }}>Скасувати</button>
                      <button onClick={addContractorAction}
                        className="flex-1 py-2 text-xs rounded-xl text-white font-semibold"
                        style={{ background: '#163248', opacity: newContractor.name.trim() ? 1 : .5 }}>Додати</button>
                    </div>
                  </div>
                )}
                {filteredContractors.length === 0 && !search && !showAddContractor ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-2">Підрядників немає</p>
                    <p className="text-sm text-gray-400">Додайте підрядника — він буде доступний у всіх проєктах</p>
                  </div>
                ) : filteredContractors.length === 0 && search ? (
                  <div className="text-center py-8"><p className="text-sm text-gray-400">Нічого не знайдено</p></div>
                ) : filteredContractors.length > 0 ? (
                <div>
                  {filteredContractors.map(c => (
                    <div key={c.id} className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50">
                      {editContractorId === c.id ? (
                        <div className="space-y-2">
                          <input type="text" value={editContractor.name} onChange={e => setEditContractor(p => ({ ...p, name: e.target.value }))}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-300" placeholder="Ім'я / компанія" autoFocus />
                          <div className="flex gap-2">
                            <input type="text" value={editContractor.phone} onChange={e => setEditContractor(p => ({ ...p, phone: e.target.value }))}
                              className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg outline-none" placeholder="Телефон" />
                            <select value={SPECS.includes(editContractor.spec) ? editContractor.spec : '__custom__'} onChange={e => {
                              if (e.target.value === '__custom__') { const v = prompt('Введіть спеціалізацію:'); if (v) setEditContractor(p => ({ ...p, spec: v })); }
                              else setEditContractor(p => ({ ...p, spec: e.target.value }));
                            }} className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg outline-none">
                              {SPECS.map(s => <option key={s}>{s}</option>)}
                              <option value="__custom__">Ввести свій варіант...</option>
                            </select>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button onClick={() => setEditContractorId(null)} className="flex-1 py-1.5 text-xs rounded-xl border border-gray-200 font-medium text-gray-500">Скасувати</button>
                            <button onClick={saveContractor} className="flex-1 py-1.5 text-xs rounded-xl text-white font-semibold" style={{ background: '#163248', opacity: editContractor.name.trim() ? 1 : .5 }}>Зберегти</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#163248' }}>
                            {c.name.charAt(0).toUpperCase()}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium" style={{ color: '#163248' }}>{c.name}</div>
                            <div className="text-[11px] text-gray-400">{c.spec || ''}{c.phone ? ' · ' + c.phone : ''}</div>
                          </div>
                          <button onClick={() => startEditContractor(c)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setConfirmModal({ show: true, title: 'Видалити підрядника?', message: `Видалити "${c.name}"?`, onConfirm: () => deleteContractor(c.id) })} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                ) : null}
              </>
            )}
          </div>
          
          {/* Action bar */}
          <div className="p-4 border-t bg-gray-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 flex-shrink-0">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              {(tab === 'items' || tab === 'market' || tab === 'lihtar') && selectedItems.length > 0 && <span>Обрано: <strong>{selectedItems.length}</strong></span>}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium" style={{ color: '#163248' }}>Закрити</button>
              {tab === 'items' && onAddToEstimate && (
                <button onClick={() => setShowSelectEstimateSection(true)} disabled={selectedItems.length === 0}
                  className={`px-5 py-2 rounded-xl text-sm font-medium ${selectedItems.length > 0 ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  Додати в кошторис
                </button>
              )}
              {(tab === 'market' || tab === 'lihtar') && onAddToEstimate && (
                <button onClick={() => setShowSelectEstimateSection(true)} disabled={selectedItems.length === 0}
                  className={`px-5 py-2 rounded-xl text-sm font-medium ${selectedItems.length > 0 ? 'text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                  style={selectedItems.length > 0 ? { backgroundColor: tab === 'lihtar' ? '#f97316' : '#163248' } : {}}>
                  Додати в кошторис
                </button>
              )}
              {tab === 'items' && setTemplates && (
                <button onClick={() => { setSelectedTemplateId(null); setSelectedTemplateSectionId(null); setNewTemplateName(''); setNewTemplateSectionName(''); setShowAddToTemplate(true); }} disabled={selectedItems.length === 0}
                  className={`px-5 py-2 rounded-xl text-sm font-medium ${selectedItems.length > 0 ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  Додати в шаблон
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== MODALS ==================== */}
      
      {/* Add Section */}
      {showAddSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4" onClick={() => { setShowAddSection(false); setNewSectionName(''); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-md w-full p-5 sm:p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Новий розділ</h3>
            <input type="text" value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="Назва розділу"
              className="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 mb-4" style={{ borderColor: '#d1d5db' }} autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && newSectionName.trim()) addSection(); }} />
            <div className="flex gap-3">
              <button onClick={addSection} disabled={!newSectionName.trim()} className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium ${newSectionName.trim() ? 'text-white hover:opacity-90' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} style={newSectionName.trim() ? { backgroundColor: '#163248' } : {}}>Створити</button>
              <button onClick={() => { setShowAddSection(false); setNewSectionName(''); }} className="px-4 py-2.5 border rounded-lg text-sm hover:bg-gray-50" style={{ color: '#6b7280', borderColor: '#d1d5db' }}>Скасувати</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Section */}
      {showRenameSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Перейменувати розділ</h3>
            <input type="text" value={renameSectionName} onChange={e => setRenameSectionName(e.target.value)} placeholder="Нова назва..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-4" autoFocus onKeyDown={e => e.key === 'Enter' && renameSection()} />
            <div className="flex gap-3">
              <button onClick={() => { setShowRenameSection(false); setRenameSectionName(''); setRenameSectionId(null); }} className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" style={{ color: '#163248' }}>Скасувати</button>
              <button onClick={renameSection} disabled={!renameSectionName.trim()} className={`flex-1 px-4 py-2.5 rounded-xl font-medium ${renameSectionName.trim() ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-200 text-gray-400'}`}>Зберегти</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item */}
      {showAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Нова позиція</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Назва</label>
                <input type="text" value={newItem.position} onChange={e => setNewItem(prev => ({ ...prev, position: e.target.value }))} placeholder="Назва позиції..." className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Тип</label>
                  <select value={newItem.type} onChange={e => setNewItem(prev => ({ ...prev, type: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    {DIRECTORY_ITEM_TYPES.map(type => <option key={type} value={type}>{type}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Одиниця</label>
                  <select value={newItem.unit} onChange={e => setNewItem(prev => ({ ...prev, unit: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}</select></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Ціна (з націнкою)</label>
                <input type="number" value={newItem.price} onChange={e => setNewItem(prev => ({ ...prev, price: e.target.value }))} onFocus={e => e.target.select()} placeholder="0" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowAddItem(false); setAddItemToSectionId(null); setNewItem({ position: '', unit: 'шт.', price: '', type: 'Робота' }); }} className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" style={{ color: '#163248' }}>Скасувати</button>
              <button onClick={addItem} disabled={!newItem.position.trim()} className={`flex-1 px-4 py-2.5 rounded-xl font-medium ${newItem.position.trim() ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-200 text-gray-400'}`}>Додати</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item */}
      {showEditItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Редагувати позицію</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Назва</label>
                <input type="text" value={editItem.position} onChange={e => setEditItem(prev => ({ ...prev, position: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Тип</label>
                  <select value={editItem.type} onChange={e => setEditItem(prev => ({ ...prev, type: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    {DIRECTORY_ITEM_TYPES.map(type => <option key={type} value={type}>{type}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Одиниця</label>
                  <select value={editItem.unit} onChange={e => setEditItem(prev => ({ ...prev, unit: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}</select></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Ціна (з націнкою)</label>
                <input type="number" value={editItem.price} onChange={e => setEditItem(prev => ({ ...prev, price: e.target.value }))} onFocus={e => e.target.select()} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowEditItem(false); setEditItemSectionId(null); }} className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" style={{ color: '#163248' }}>Скасувати</button>
              <button onClick={saveEditItem} disabled={!editItem.position.trim()} className={`flex-1 px-4 py-2.5 rounded-xl font-medium ${editItem.position.trim() ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-200 text-gray-400'}`}>Зберегти</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contractor — handled inline below */}

      {/* Select Estimate Section */}
      {showSelectEstimateSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-1" style={{ color: '#163248' }}>Куди додати?</h3>
            <p className="text-sm text-gray-500 mb-4">Оберіть розділ кошторису для обраних позицій</p>
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {(estimateSections || []).map(s => (
                <button key={s.id} onClick={() => handleAddToEstimate(s.id)} className="w-full text-left px-4 py-3 border border-gray-200 rounded-xl hover:bg-amber-50 hover:border-amber-300 text-sm font-medium transition-colors" style={{ color: '#163248' }}>
                  {s.name}
                </button>
              ))}
              <button onClick={() => { const name = newEstimateSectionName.trim() || 'Новий розділ'; handleAddToEstimate(null, true, name); }} className="w-full text-left px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:bg-gray-50 text-sm text-gray-500">
                + Новий розділ
              </button>
            </div>
            <button onClick={() => setShowSelectEstimateSection(false)} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-sm font-medium" style={{ color: '#163248' }}>Скасувати</button>
          </div>
        </div>
      )}

      {/* Add to Template */}
      {showAddToTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Додати до шаблону</h3>
            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
              {templates.map(t => (
                <div key={t.id} className={`border rounded-xl overflow-hidden ${selectedTemplateId === t.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                  <button onClick={() => { setSelectedTemplateId(t.id); setSelectedTemplateSectionId(null); }} className="w-full text-left px-4 py-2.5 text-sm font-medium" style={{ color: '#163248' }}>{t.name}</button>
                  {selectedTemplateId === t.id && (
                    <div className="px-4 pb-3 space-y-1">
                      {(t.sections || []).map(s => (
                        <button key={s.id} onClick={() => setSelectedTemplateSectionId(s.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs ${selectedTemplateSectionId === s.id ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
                          {s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => { setSelectedTemplateId('new'); setNewTemplateName(''); }} className="w-full text-left px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:bg-gray-50 text-sm text-gray-500">+ Новий шаблон</button>
              {selectedTemplateId === 'new' && (
                <input type="text" value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} placeholder="Назва шаблону..." className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowAddToTemplate(false)} className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" style={{ color: '#163248' }}>Скасувати</button>
              <button onClick={() => {
                const itemsToAdd = [];
                sections.forEach(section => { section.items.forEach(item => {
                  if (selectedItems.includes(`${section.id}-${item.id}`)) {
                    itemsToAdd.push({ id: Date.now() + Math.random(), type: item.type || 'Робота', position: item.position, unit: item.unit || 'шт.', qty: 1, workPrice: 0, markup: 0, clientPrice: Math.round((item.price || 0) * 100) / 100, priceLock: true });
                  }
                }); });
                if (itemsToAdd.length === 0) return;
                if (selectedTemplateId === 'new' && newTemplateName.trim()) {
                  const newT = { id: Date.now(), name: newTemplateName.trim(), sections: [{ id: Date.now() + 1, name: 'Розділ 1', items: itemsToAdd }] };
                  setTemplates(prev => [...prev, newT]);
                } else if (selectedTemplateId && selectedTemplateSectionId) {
                  setTemplates(prev => prev.map(t => t.id === selectedTemplateId ? { ...t, sections: t.sections.map(s => s.id === selectedTemplateSectionId ? { ...s, items: [...s.items, ...itemsToAdd] } : s) } : t));
                }
                setSelectedItems([]); setShowAddToTemplate(false);
              }} className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium">Додати</button>
            </div>
          </div>
        </div>
      )}
      {/* Export PDF Modal — вибір розділів перед генерацією */}
      <DirectoryExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        sections={sections}
        onExport={(exportSections) => {
          if (exportSections.length === 0) return;
          const html = generateDirectoryPDF({ sections: exportSections, branding });
          const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
          const blobUrl = URL.createObjectURL(blob);
          if (setPreviewFile) {
            onClose();
            setTimeout(() => {
              setPreviewFile({
                name: 'Прайс-лист.pdf',
                data: blobUrl,
                printHtml: html,
                isPrintPreview: true,
              });
            }, 100);
          } else {
            window.open(blobUrl, '_blank');
          }
        }}
      />
      {/* Share Link Modal */}
      {showShareModal && (() => {
        const publicCount = sections.filter(s => !s.is_private).length;
        const includedCount = sections.filter(s => !s.is_private && !excludedShareIds.has(s.id)).length;
        const selectionUrl = buildShareUrl(true);
        const fullUrl = buildShareUrl(false);
        const sameUrl = selectionUrl === fullUrl;
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: '#163248' }}>Онлайн прайс-лист</h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Обрано <span style={{ color: '#163248', fontWeight: 600 }}>{includedCount}</span> з {publicCount} публічних розділів. Приватні ніколи не потраплять у посилання.
            </p>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border mb-2">
              <input type="text" value={selectionUrl} readOnly className="flex-1 text-sm bg-transparent outline-none text-gray-700 truncate" />
              <button onClick={copyShareLink} disabled={includedCount === 0}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${shareCopied ? 'bg-green-100 text-green-700' : 'bg-white border border-gray-200 hover:bg-gray-100 text-gray-700'} ${includedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {shareCopied ? <><Check className="w-3.5 h-3.5" /> Скопійовано</> : <><Copy className="w-3.5 h-3.5" /> Копіювати</>}
              </button>
            </div>
            {!sameUrl && (
              <button onClick={copyFullShareLink} className="w-full text-xs text-gray-500 hover:text-gray-700 underline py-1">
                {shareFullCopied ? '✓ Скопійовано повне посилання' : 'Копіювати повне посилання (усі публічні розділи)'}
              </button>
            )}
          </div>
        </div>
        );
      })()}
      {/* Add Market Item to Directory — section picker */}
      {showAddMarketToDir && pendingMarketItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4" onClick={() => { setShowAddMarketToDir(false); setPendingMarketItem(null); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-md w-full p-5 sm:p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1" style={{ color: '#163248' }}>Додати в довідник</h3>
            <p className="text-sm text-gray-500 mb-4">{pendingMarketItem.position}</p>
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {sections.map(s => (
                <button key={s.id} onClick={async () => {
                  const { data: { user } } = await supabase.auth.getUser();
                  if (!user) return;
                  const avgPrice = Math.round((pendingMarketItem.min + pendingMarketItem.max) / 2);
                  const row = { section_id: s.id, user_id: user.id, position: pendingMarketItem.position, unit: pendingMarketItem.unit, price: avgPrice, type: 'Робота', sort_order: 0 };
                  const { data: newItem } = await supabase.from('user_directory_items').insert(row).select().single();
                  if (newItem) {
                    setSections(prev => prev.map(sec => sec.id === s.id ? { ...sec, items: [...sec.items, newItem] } : sec));
                    if (window.showToast) window.showToast(`"${pendingMarketItem.position}" додано в "${s.name}"`);
                  }
                  setShowAddMarketToDir(false); setPendingMarketItem(null);
                }} className="w-full text-left px-4 py-3 border border-gray-200 rounded-xl hover:bg-amber-50 hover:border-amber-300 text-sm font-medium transition-colors" style={{ color: '#163248' }}>
                  {s.name}
                </button>
              ))}
              {/* Новий розділ */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl px-4 py-3">
                <div className="flex gap-2">
                  <input type="text" value={newMarketDirSectionName} onChange={e => setNewMarketDirSectionName(e.target.value)}
                    placeholder="Новий розділ..." className="flex-1 text-sm outline-none" autoFocus={sections.length === 0}
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && newMarketDirSectionName.trim()) {
                        const { data: { user } } = await supabase.auth.getUser();
                        if (!user) return;
                        const { data: newSec } = await supabase.from('user_directory').insert({ user_id: user.id, name: newMarketDirSectionName.trim(), sort_order: sections.length }).select().single();
                        if (newSec) {
                          const avgPrice = Math.round((pendingMarketItem.min + pendingMarketItem.max) / 2);
                          const row = { section_id: newSec.id, user_id: user.id, position: pendingMarketItem.position, unit: pendingMarketItem.unit, price: avgPrice, type: 'Робота', sort_order: 0 };
                          const { data: newItem } = await supabase.from('user_directory_items').insert(row).select().single();
                          setSections(prev => [...prev, { ...newSec, items: newItem ? [newItem] : [] }]);
                          if (window.showToast) window.showToast(`"${pendingMarketItem.position}" додано в "${newMarketDirSectionName.trim()}"`);
                        }
                        setShowAddMarketToDir(false); setPendingMarketItem(null);
                      }
                    }} />
                  <button onClick={async () => {
                    if (!newMarketDirSectionName.trim()) return;
                    const { data: { user } } = await supabase.auth.getUser();
                    if (!user) return;
                    const { data: newSec } = await supabase.from('user_directory').insert({ user_id: user.id, name: newMarketDirSectionName.trim(), sort_order: sections.length }).select().single();
                    if (newSec) {
                      const avgPrice = Math.round((pendingMarketItem.min + pendingMarketItem.max) / 2);
                      const row = { section_id: newSec.id, user_id: user.id, position: pendingMarketItem.position, unit: pendingMarketItem.unit, price: avgPrice, type: 'Робота', sort_order: 0 };
                      const { data: newItem } = await supabase.from('user_directory_items').insert(row).select().single();
                      setSections(prev => [...prev, { ...newSec, items: newItem ? [newItem] : [] }]);
                      if (window.showToast) window.showToast(`"${pendingMarketItem.position}" додано в "${newMarketDirSectionName.trim()}"`);
                    }
                    setShowAddMarketToDir(false); setPendingMarketItem(null);
                  }} disabled={!newMarketDirSectionName.trim()} className={`px-3 py-1 rounded-lg text-xs font-medium ${newMarketDirSectionName.trim() ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                    Створити
                  </button>
                </div>
              </div>
            </div>
            <button onClick={() => { setShowAddMarketToDir(false); setPendingMarketItem(null); }} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-sm font-medium" style={{ color: '#163248' }}>Скасувати</button>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== ADD TO DIRECTORY MODAL ====================

const AddToDirectoryModal = ({ isOpen, onClose, selectedItems, items }) => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [showNewSectionInput, setShowNewSectionInput] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      (async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase.from('user_directory').select('*').eq('user_id', user.id).order('sort_order');
        setSections(data || []);
        setSelectedSection(''); setNewSectionName(''); setShowNewSectionInput(false);
      })();
    }
  }, [isOpen]);
  
  const handleAdd = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let targetSectionId = selectedSection;
    
    if (showNewSectionInput && newSectionName.trim()) {
      const { data: newSec } = await supabase.from('user_directory').insert({ user_id: user.id, name: newSectionName.trim(), sort_order: sections.length }).select().single();
      if (newSec) targetSectionId = newSec.id;
    }
    
    if (!targetSectionId) return;
    
    const itemsToAdd = [];
    items.forEach(section => {
      section.items.forEach(item => {
        if (selectedItems.includes(`${section.id}-${item.id}`)) {
          const clientPrice = item.clientPrice !== undefined ? item.clientPrice : (item.workPrice || 0) * (1 + (item.markup || 20) / 100);
          itemsToAdd.push({
            section_id: targetSectionId, user_id: user.id,
            position: item.position, unit: item.unit || 'шт.',
            price: Math.round(clientPrice * 100) / 100, type: item.type || 'Робота', sort_order: 0
          });
        }
      });
    });
    
    if (itemsToAdd.length === 0) return;
    await supabase.from('user_directory_items').insert(itemsToAdd);
    onClose();
  };
  
  if (!isOpen) return null;
  const itemCount = selectedItems.length;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
        <h3 className="text-lg font-semibold mb-1" style={{ color: '#163248' }}>Додати до довідника</h3>
        <p className="text-sm text-gray-500 mb-4">{itemCount} {itemCount === 1 ? 'позиція' : itemCount < 5 ? 'позиції' : 'позицій'}</p>
        
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Оберіть розділ довідника</label>
          {sections.map(s => (
            <button key={s.id} onClick={() => { setSelectedSection(s.id); setShowNewSectionInput(false); }}
              className={`w-full text-left px-4 py-3 border rounded-xl text-sm font-medium transition-colors ${selectedSection === s.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`} style={{ color: '#163248' }}>
              {s.name}
            </button>
          ))}
          
          {!showNewSectionInput ? (
            <button onClick={() => { setShowNewSectionInput(true); setSelectedSection(''); }} className="w-full text-left px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:bg-gray-50 text-sm text-gray-500">
              + Новий розділ
            </button>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2"><Plus className="w-4 h-4 text-amber-600" /><span className="text-sm font-medium text-amber-700">Новий розділ</span></div>
              <input type="text" value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="Назва нового розділу..."
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" autoFocus />
            </div>
          )}
        </div>
        
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" style={{ color: '#163248' }}>Скасувати</button>
          <button onClick={handleAdd} disabled={!selectedSection && !(showNewSectionInput && newSectionName.trim())}
            className={`flex-1 px-4 py-2.5 rounded-xl font-medium ${selectedSection || (showNewSectionInput && newSectionName.trim()) ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
            Додати
          </button>
        </div>
      </div>
    </div>
  );
};

export { DirectoryModal, AddToDirectoryModal };
